import React, { useContext, useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  ActivityLayout, 
  ActivityTimer, 
  ActivityProgress,
  useActivityData,
  activityApiService,
  formatTime,
  generateRemarks
} from '../shared';
import { UserContext } from '../../UserContext';
import { fetchActivitiesImage } from '../fetchActivitiesImage';
import WrittenActivityImage from './activity.png'; 
import GoodImage from './good.png';
import CrossImage from './cross.png';
import FinishedImage from './finished.jpeg';
import './WrittenActivity.css';

const WrittenActivity = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activityId = searchParams.get("activityId");

  // Use shared activity data hook
  const {
    actualQuestions,
    currQuestion,
    count,
    totalQuestions,
    marksPerQuestion,
    totalMarks,
    questionNumber,
    gainedMarks,
    items,
    studentId,
    setCount,
    setQuestionNumber,
    setCurrQuestion,
    setMarksPerQuestion,
    setItems,
    setGainedMarks,
    countRef,
    totalQuestionsRef,
    progressIncrement,
    questionNumberRef,
    progressCount,
    gainedMarksRef,
    totalTimeTakenOfAllQuestions,
    totalTimeOfAllQuestions,
    completeActivityRef
  } = useActivityData(activityId);

  // Written Activity specific state
  const [imageData, setImageData] = useState(null);
  const [studentAnswer, setStudentAnswer] = useState('');
  const [isFinished, setIsFinished] = useState(false);

  // Refs for Written Activity specific elements
  const submitButton = useRef(null);
  const GoodImageRef = useRef(null);
  const CrossImageRef = useRef(null);
  const FinishedImageRef = useRef(null);
  const ProgressRef = useRef(null);
  const studentCurrAnswerRef = useRef('');
  const marksObtainedCurrQuestionRef = useRef(0);
  const isCorrectRef = useRef(0);

  const { userInfo } = useContext(UserContext);
  const navigate = useNavigate();

  // Fetch image data when current question changes
  useEffect(() => {
    if (currQuestion?.questionpicture) {
      fetchActivitiesImage(setImageData, currQuestion.questionpicture);
    }
  }, [currQuestion]);

  useEffect(() => {
    let intervalId;
    if (!isFinished) {
      intervalId = setInterval(() => {
        countRef.current -= 1;
        if (countRef.current <= -1) {
          studentCurrAnswerRef.current = '';
          marksObtainedCurrQuestionRef.current = 0;
          isCorrectRef.current = 0;
          submitStudentQuestionResponse();

          if (questionNumber < totalQuestions) {
            questionNumberRef.current += 1;
            setQuestionNumber(prev => prev + 1);
            setCurrQuestion(actualQuestions[questionNumberRef.current]);
            setMarksPerQuestion(actualQuestions[questionNumberRef.current].marks);
            setCount(actualQuestions[questionNumberRef.current].timealloted);
            countRef.current = actualQuestions[questionNumberRef.current].timealloted;
            setItems(prev => [...prev, 0]);
            if (GoodImageRef.current) if (GoodImageRef.current) GoodImageRef.current.style.display = 'none';
            if (CrossImageRef.current) if (CrossImageRef.current) CrossImageRef.current.style.display = 'inline-block';
            progressCount.current += progressIncrement.current;
            if (ProgressRef.current) if (ProgressRef.current) ProgressRef.current.style.width = `${progressCount.current}%`;
          } else {
            clearInterval(intervalId);
            if (submitButton.current) if (submitButton.current) submitButton.current.style.display = 'none';
            if (FinishedImageRef.current) if (FinishedImageRef.current) FinishedImageRef.current.style.display = 'inline-block';
            setIsFinished(true);
          }
        } else {
          setCount(countRef.current);
        }
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [questionNumber, count, actualQuestions, totalQuestions, isFinished]);

  useEffect(() => {
    if (questionNumber === totalQuestions && count === 0) {
      studentCurrAnswerRef.current = '';
      marksObtainedCurrQuestionRef.current = 0;
      isCorrectRef.current = 0;
      submitStudentQuestionResponse();
      progressCount.current += progressIncrement.current;
      if (ProgressRef.current) if (ProgressRef.current) ProgressRef.current.style.width = `${progressCount.current}%`;
      setItems(prev => [...prev, 0]);
      if (submitButton.current) if (submitButton.current) submitButton.current.style.display = 'none';
      if (FinishedImageRef.current) if (FinishedImageRef.current) FinishedImageRef.current.style.display = 'inline-block';
      if (GoodImageRef.current) if (GoodImageRef.current) GoodImageRef.current.style.display = 'none';
      if (CrossImageRef.current) if (CrossImageRef.current) CrossImageRef.current.style.display = 'inline-block';
      setIsFinished(true);
    }
  }, [questionNumber, count, totalQuestions]);

  const handleStudentAnswer = (event) => {
    setStudentAnswer(event.target.value);
  };

  const submitStudentQuestionResponse = async () => {
    totalTimeTakenOfAllQuestions.current += currQuestion.timealloted - countRef.current - 1;
    totalTimeOfAllQuestions.current += currQuestion.timealloted;

    try {
      await activityApiService.saveActivityQuestion({
        Question_id: currQuestion.Question_id,
        Student_id: studentId,
        timetaken: currQuestion.timealloted - countRef.current - 1,
        StudentAnswer: studentCurrAnswerRef.current,
        MarksObtained: marksObtainedCurrQuestionRef.current,
        TotalTime: currQuestion.timealloted,
        correct: isCorrectRef.current,
      });

      await activityApiService.saveActivity({
        Activity_id: activityId,
        Student_id: studentId,
        MarksObtained: gainedMarksRef.current,
        MarksObtainedPercentage: (gainedMarksRef.current / totalMarks) * 100,
        Remarks: generateRemarks((gainedMarksRef.current / totalMarks) * 100),
        SubmitActivityFile: '',
        TimeTaken: totalTimeTakenOfAllQuestions.current,
        TotalMarks: totalMarks,
        TotalTime: totalTimeOfAllQuestions.current < 0 ? 0 : totalTimeOfAllQuestions.current,
        Complete: completeActivityRef.current,
      });
    } catch (error) {
      console.error('Error submitting response:', error);
    }
  };

  const submitCompleteActivityResponse = async () => {
    try {
      await activityApiService.saveActivity({
        Activity_id: activityId,
        Student_id: studentId,
        MarksObtained: gainedMarksRef.current,
        MarksObtainedPercentage: (gainedMarksRef.current / totalMarks) * 100,
        Remarks: 'Good Efforts',
        SubmitActivityFile: '',
        TimeTaken: totalTimeTakenOfAllQuestions.current,
        TotalMarks: totalMarks,
        TotalTime: totalTimeOfAllQuestions.current,
        Complete: completeActivityRef.current,
      });
    } catch (error) {
      console.error('Error submitting complete activity:', error);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!studentAnswer || studentAnswer.trim() === '') {
      alert('Please enter an answer before submitting!');
      return;
    }

    if (questionNumber === totalQuestions) {
      if (studentAnswer === currQuestion?.correctanswer) {
        setItems(prev => [...prev, 1]);
        gainedMarksRef.current += marksPerQuestion;
        setGainedMarks(prev => prev + marksPerQuestion);
        if (CrossImageRef.current) CrossImageRef.current.style.display = 'none';
        if (GoodImageRef.current) GoodImageRef.current.style.display = 'inline-block';
        studentCurrAnswerRef.current = studentAnswer;
        marksObtainedCurrQuestionRef.current = marksPerQuestion;
        isCorrectRef.current = 1;
      } else {
        setItems(prev => [...prev, 0]);
        if (GoodImageRef.current) GoodImageRef.current.style.display = 'none';
        if (CrossImageRef.current) CrossImageRef.current.style.display = 'inline-block';
        studentCurrAnswerRef.current = studentAnswer;
        marksObtainedCurrQuestionRef.current = 0;
        isCorrectRef.current = 0;
      }
      submitStudentQuestionResponse();
      if (submitButton.current) submitButton.current.style.display = 'none';
      if (FinishedImageRef.current) FinishedImageRef.current.style.display = 'inline-block';
      progressCount.current += progressIncrement.current;
      if (ProgressRef.current) ProgressRef.current.style.width = `${progressCount.current}%`;
      completeActivityRef.current = 1;
      submitCompleteActivityResponse();
      setIsFinished(true);
    } else {
      if (studentAnswer === currQuestion?.correctanswer) {
        setItems(prev => [...prev, 1]);
        gainedMarksRef.current += marksPerQuestion;
        setGainedMarks(prev => prev + marksPerQuestion);
        if (CrossImageRef.current) CrossImageRef.current.style.display = 'none';
        if (GoodImageRef.current) GoodImageRef.current.style.display = 'inline-block';
        studentCurrAnswerRef.current = studentAnswer;
        marksObtainedCurrQuestionRef.current = marksPerQuestion;
        isCorrectRef.current = 1;
      } else {
        setItems(prev => [...prev, 0]);
        if (GoodImageRef.current) GoodImageRef.current.style.display = 'none';
        if (CrossImageRef.current) CrossImageRef.current.style.display = 'inline-block';
        studentCurrAnswerRef.current = studentAnswer;
        marksObtainedCurrQuestionRef.current = 0;
        isCorrectRef.current = 0;
      }
      submitStudentQuestionResponse();
      questionNumberRef.current += 1;
      setQuestionNumber(prev => prev + 1);
      setCurrQuestion(actualQuestions[questionNumberRef.current]);
      setMarksPerQuestion(actualQuestions[questionNumberRef.current].marks);
      setCount(actualQuestions[questionNumberRef.current].timealloted);
      countRef.current = actualQuestions[questionNumberRef.current].timealloted;
      progressCount.current += progressIncrement.current;
      if (ProgressRef.current) ProgressRef.current.style.width = `${progressCount.current}%`;
    }
    setStudentAnswer('');
  };

  const onLogout = () => {
    fetch(`${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/logout`, {
      credentials: "include",
      method: "POST",
    });
    navigate("/");
  };

  const handleCourseSelect = (course) => {
    console.log("Selected Course:", course);
  };

  return (
    <ActivityLayout
      title="Written Activity"
      onLogout={onLogout}
      onCourseSelect={handleCourseSelect}
    >
      <div className="MAMheading">
        <div className="MAMactivity-image">
          <img src={WrittenActivityImage} alt="" />
        </div>

        <div className="MAMactivity-text">
          <h3>{currQuestion?.ActivityName}</h3>
        </div>
      </div>

      <div className="MAMcontainer">
        <div className="MAMbody">
          <div className="left-column">
            <div className="question-tracker">
              <p>
                Question
                <span className="question-number">#{questionNumber}</span>
              </p>
              <ActivityProgress 
                current={questionNumber}
                total={totalQuestions}
                progress={(questionNumber / totalQuestions) * 100}
              />
            </div>
            <div className="mobile-timer">
              <ActivityTimer count={count} displayFormat="box" />
            </div>
            <div className="MAMpicture">
              {imageData ? (
                <img src={imageData} alt="Question Image" />
              ) : (
                <p></p>
              )}
            </div>
            <div className="question-content">
            <h5 className="question-text">{currQuestion?.question}</h5>
            {imageData && (
              <div className="question-image-container">
                <img src={imageData} alt="Question Image" className="question-image" />
              </div>
            )}
          </div>
            <div class="MAOanswer-submit">
              <input
                id="MAstudent-answer"
                class="MAOanswer"
                type="text"
                ref={submitButton}
                placeholder="Type Answer here..."
                value={studentAnswer}
                onChange={handleStudentAnswer}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && questionNumber != totalQuestions && count != 0) {
                    handleSubmit(e);
                  }
                }}
              />
              <input
                id="MAsubmission"
                type="submit"
                ref={submitButton}
                onClick={handleSubmit}
              />
            </div>
            <div class="MAremarks">
              <div class="MAcorrect-wrong-picture">
                <img
                  id="MAremarksId"
                  src={GoodImage}
                  ref={GoodImageRef}
                  alt=""
                  style={{ width: "100px", display: "none" }}
                />
                <img
                  id="remarksId"
                  src={CrossImage}
                  ref={CrossImageRef}
                  alt=""
                  style={{ width: "100px", display: "none" }}
                />
              </div>

              <div class="MAfinished-picture">
                <img
                  id="finishedId"
                  src={FinishedImage}
                  ref={FinishedImageRef}
                  alt=""
                  style={{ width: "100px", display: "none" }}
                />
              </div>
            </div>
          </div>

          <div className="right-column">
            <div className="desktop-timer">
              <ActivityTimer count={count} displayFormat="box" />
            </div>
            <div id="MAmarks-color">
              <h3 className="MAMscoreHeading">Score</h3>
              <h3 className="MAMscoreHeading" style={{ display: "flex" }}>
                <span id="MAOcurrent-score">{gainedMarks}</span> /
                <span id="MAOtotal-score">{totalMarks}</span>
              </h3>
              {items.map((item, index) => (
                <div className="MAbox">
                  <div className="MAbox1">
                    <h3>{(index + 1).toString().padStart(2, "0")}</h3>
                  </div>
                  <div className={`MAbox2${item}`}>
                    {item === 1 ? "✓" : "✗"}
                  </div>
                </div>
              ))}
            </div>
            {currQuestion?.instructions && (
              <div className="">
                <span
                  style={{
                    color: "#b8860b",
                    fontWeight: "bold",
                    cursor: "pointer",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    display: "inline-block",
                    margin: "8px 0",
                    display: "flex"
                  }}
                  onClick={() => {
                    window.alert(currQuestion?.instructions);
                  }}
                >
                  Read instruction
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ActivityLayout>
  );
};

export default WrittenActivity;